import { Brain, Route, Snowflake, Zap, Database } from 'lucide-react';

/**
 * AI Inference Intelligence Section
 *
 * Key message: The intelligence layer for GPU inference
 *
 * Forecaster is NOT just prediction - it's the complete intelligence layer:
 * - Predict demand
 * - Route intelligently (SLO + cost aware)
 * - Manage GPU lifecycle (hibernate, pre-warm)
 * - Optimize resources
 * - Arbitrage across providers
 */

// Feature card
interface FeatureProps {
    icon: React.ElementType;
    title: string;
    description: string;
}

const Feature = ({ icon: Icon, title, description }: FeatureProps) => (
    <div className="flex gap-4 p-6 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-volt-500/30 transition-colors">
        <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-volt-500/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-volt-400" />
        </div>
        <div>
            <h4 className="text-lg font-semibold text-white mb-2">{title}</h4>
            <p className="text-sm text-gray-400 leading-relaxed">{description}</p>
        </div>
    </div>
);

// Prediction engine visual
const PredictionVisual = () => {
    const capabilities = [
        'Traffic patterns',
        'Seasonal trends',
        'Anomaly detection',
        'Growth patterns',
    ];

    return (
        <div className="relative p-8 rounded-3xl border border-white/[0.06] bg-white/[0.02]">
            {/* Background glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-volt-500/5 via-transparent to-transparent rounded-3xl" />

            <div className="relative">
                {/* Header */}
                <div className="text-center mb-6">
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-volt-500/10 border border-volt-500/20 mb-4">
                        <Brain className="w-5 h-5 text-volt-400" />
                        <span className="text-volt-400 font-medium">Predictive Engine</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">Accurate predictions. Always adapting.</h3>
                    <p className="text-gray-400 text-sm">Learns complex traffic patterns across multiple dimensions. Continuously learning from your live traffic.</p>
                </div>

                {/* What patterns we detect */}
                <div className="mb-6">
                    <div className="text-xs text-gray-500 uppercase tracking-wider mb-3 text-center">Patterns detected</div>
                    <div className="flex flex-wrap justify-center gap-2">
                        {capabilities.map((cap, i) => (
                            <span key={i} className="px-3 py-1.5 rounded-full bg-obsidian-800 border border-white/[0.06] text-sm text-gray-300">
                                {cap}
                            </span>
                        ))}
                    </div>
                </div>

                {/* How it works */}
                <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-obsidian-800">
                        <div className="w-8 h-8 rounded-full bg-volt-500/20 flex items-center justify-center text-volt-400 font-bold text-sm">1</div>
                        <div className="text-sm text-gray-300">Learns your traffic patterns from day one</div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-obsidian-800">
                        <div className="w-8 h-8 rounded-full bg-volt-500/20 flex items-center justify-center text-volt-400 font-bold text-sm">2</div>
                        <div className="text-sm text-gray-300">Predicts demand ahead with high confidence</div>
                    </div>
                    <div className="flex items-center gap-3 p-3 rounded-lg bg-obsidian-800">
                        <div className="w-8 h-8 rounded-full bg-volt-500/20 flex items-center justify-center text-volt-400 font-bold text-sm">3</div>
                        <div className="text-sm text-gray-300">Adapts continuously — accuracy improves over time</div>
                    </div>
                </div>

                {/* Callout */}
                <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-volt-500/10 to-transparent border border-volt-500/20">
                    <p className="text-sm text-center text-gray-300">
                        <span className="text-white font-medium">Zero external dependencies. Self-contained.</span> Pure inference speed.
                    </p>
                </div>
            </div>
        </div>
    );
};

export const AIIntelligenceSection = () => {
    const features = [
        {
            icon: Zap,
            title: 'Proactive scaling',
            description: 'Horizontal Pod Autoscaling reacts 2-3 minutes late. Forecaster scales proactively — not reactive guesses.',
        },
        {
            icon: Route,
            title: 'Continuous learning',
            description: 'Models learn from live traffic. Drift detected and corrected automatically. Always adapting to your workload.',
        },
        {
            icon: Snowflake,
            title: 'Cold start elimination',
            description: 'Intelligent caching keeps models warm. GPUs pre-loaded before traffic arrives. Users never wait for scale-up.',
        },
        {
            icon: Database,
            title: 'Safety built in',
            description: 'Predictions validated before reaching production. Automatic fallback to reactive scaling on low confidence.',
        },
    ];

    return (
        <section className="py-24 lg:py-32 bg-obsidian-900">
            <div className="max-w-7xl mx-auto px-6">
                {/* Section header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-volt-500/10 border border-volt-500/20 text-volt-400 text-sm font-medium mb-6">
                        <Brain className="w-4 h-4" />
                        Predictive Intelligence
                    </div>
                    <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight">
                        ML predicts demand.
                        <br />
                        <span className="text-gray-500">You scale before users notice.</span>
                    </h2>
                    <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                        While Horizontal Pod Autoscaling reacts 2-3 minutes late, Forecaster predicts ahead of demand.
                        GPUs are warm and ready when traffic arrives.
                    </p>
                </div>

                {/* Two column layout */}
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    {/* Left: Prediction visual */}
                    <div className="lg:sticky lg:top-32">
                        <PredictionVisual />
                    </div>

                    {/* Right: Features */}
                    <div className="space-y-4">
                        {features.map((feature, i) => (
                            <Feature key={i} {...feature} />
                        ))}

                        {/* Bold statement */}
                        <div className="mt-8 p-6 rounded-xl bg-gradient-to-r from-volt-500/10 to-transparent border border-volt-500/20">
                            <p className="text-lg text-white font-medium text-center">
                                Every request. Every GPU. Every dollar. Optimized.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Section divider */}
                <div className="max-w-xl mx-auto mt-24">
                    <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                </div>
            </div>
        </section>
    );
};
